import { FC, useState } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import CustomButton from '../CustomButton';
import ImageUploader from '../ImageUploader/ImageUploader';

import styles from './CoverUploadModal.module.scss';

export interface CoverUploadModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  placeholder?: {
    title?: string;
    subtitle?: string;
  };
  uploadFile: (file: File) => Promise<unknown>;
  zIndex?: number;
}

const CoverUploadModal: FC<CoverUploadModalProps> = ({
  open,
  onClose,
  title,
  description = 'Choose a new image for your cover.',
  placeholder = {
    title: 'Select your cover',
    subtitle: '(e.g., PNG, JPG, WEBP up to 5MB)',
  },
  uploadFile,
  zIndex,
}) => {
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleImageChange = (file: File | null) => {
    setSelectedImage(file);
    if (uploadError) {
      setUploadError(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedImage) return;

    try {
      setUploadError(null);
      setIsLoading(true);
      await uploadFile(selectedImage);
      handleClose();
    } catch (error: unknown) {
      const err = error as { data?: { message?: string; image?: string[] }; message?: string };
      const errorMessage =
        err?.data?.message || err?.data?.image?.[0] || err?.message || 'Failed to upload cover';
      setUploadError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedImage(null);
    setUploadError(null);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="cover-upload-modal"
      sx={{
        ...(zIndex != null ? { zIndex } : {}),
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(34, 43, 58, 0.2)',
        },
      }}
    >
      <Box className={styles.modalContainer}>
        <Box className={styles.modalHeader}>
          <Typography
            variant="h5Bold"
            className={styles.modalTitle}
          >
            {title}
          </Typography>

          <Box
            className={styles.closeButton}
            onClick={handleClose}
          >
            <CloseIcon sx={{ width: '24px', height: '24px' }} />
          </Box>
        </Box>

        <Box className={styles.modalContent}>
          <Typography
            variant="h10"
            sx={{ color: '#5E6A81', textAlign: 'center', mb: 3 }}
          >
            {description}
          </Typography>

          <ImageUploader
            image={selectedImage}
            onImageChange={handleImageChange}
            disabled={isLoading}
            placeholder={placeholder}
          />

          {uploadError ? (
            <Typography
              variant="h12"
              sx={{ color: '#C8102E', textAlign: 'center', mt: 2 }}
            >
              {uploadError}
            </Typography>
          ) : null}
        </Box>

        <Box className={styles.modalFooter}>
          <CustomButton
            variant="outlined"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </CustomButton>

          <CustomButton
            variant="contained"
            onClick={handleSubmit}
            disabled={!selectedImage || isLoading}
          >
            {isLoading ? 'Uploading...' : 'Upload Cover'}
          </CustomButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default CoverUploadModal;
